<template>
  <div class="sso-authorize-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-wrapper">
      <i class="el-icon-loading loading-icon"></i>
      <p class="loading-text">Memproses permintaan...</p>
    </div>

    <!-- Authorization Form -->
    <el-card v-else-if="!loading && clientInfo" class="authorize-card" shadow="always">
    <div class="card-header">
      <div class="header-icon">
        <i class="el-icon-lock"></i>
      </div>
      <h2 class="header-title" style="text-align: center; display: flex; justify-content: center; align-items: center;">Otorisasi Akses SSO Intranet</h2>
    </div>

      <div class="card-content">
        <!-- Client Info -->
        <div class="client-info">
          <div class="client-name-wrapper">
            <el-tag type="primary" size="large" effect="plain">
              {{ clientInfo.clientName }}
            </el-tag>
          </div>
          
          <el-alert
            type="info"
            :closable="false"
            class="info-alert"
          >
            <strong>{{ clientInfo.clientName }}</strong> meminta izin untuk mengakses informasi Anda:
          </el-alert>

          <!-- Permissions List -->
          <div class="permissions-list">
            <div class="permission-item">
              <div class="permission-icon">
                <i class="el-icon-user"></i>
              </div>
              <div class="permission-content">
                <div class="permission-title">Informasi Identitas</div>
                <div class="permission-subtitle">
                  Nama lengkap, username, email, dan NIP (Nomor Induk Pegawai)
                </div>
                <div class="permission-data" v-if="userName || userNip">
                  <span v-if="userName">{{ userName }}</span>
                  <span v-if="userNip" class="nip-badge">NIP: {{ userNip }}</span>
                </div>
              </div>
            </div>

            <div class="permission-item">
              <div class="permission-icon">
                <i class="el-icon-office-building"></i>
              </div>
              <div class="permission-content">
                <div class="permission-title">Informasi Kepegawaian</div>
                <div class="permission-subtitle">
                  Jabatan, unit kerja, eselon, dan informasi organisasi
                </div>
                <div class="permission-data" v-if="userJabatan || userUnitKerja">
                  <span v-if="userJabatan" class="jabatan-badge">{{ userJabatan }}</span>
                  <span v-if="userUnitKerja" class="unit-badge">{{ userUnitKerja }}</span>
                </div>
              </div>
            </div>

            <div class="permission-item">
              <div class="permission-icon">
                <i class="el-icon-key"></i>
              </div>
              <div class="permission-content">
                <div class="permission-title">Status Autentikasi</div>
                <div class="permission-subtitle">
                  Mempertahankan status login dan mengakses aplikasi tanpa login ulang
                </div>
              </div>
            </div>
          </div>
        </div>


        <!-- Error Message -->
        <el-alert
          v-if="error"
          type="error"
          :closable="true"
          class="error-alert"
          @close="error = null"
        >
          {{ error }}
        </el-alert>

        <!-- Additional Info -->
        <div class="additional-info">
          <p class="redirect-info">
            Anda akan diarahkan kembali ke <strong>{{ clientInfo.clientName }}</strong>
          </p>
          <p class="revoke-info">
            <i class="el-icon-info"></i>
            Anda dapat mencabut akses kapan saja dari pengaturan akun
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <el-button
            type="primary"
            size="large"
            class="authorize-btn"
            :loading="authorizing"
            :disabled="authorizing || denying"
            @click="handleAuthorize"
          >
            <i class="el-icon-check"></i>
            Izinkan Akses
          </el-button>
          
          <el-button
            type="danger"
            size="large"
            plain
            class="deny-btn"
            :loading="denying"
            :disabled="authorizing || denying"
            @click="handleDeny"
          >
            <i class="el-icon-close"></i>
            Tolak
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- Error State -->
    <el-card v-else class="error-card" shadow="always">
      <div class="error-content">
        <i class="el-icon-warning error-icon"></i>
        <h3 class="error-title">Permintaan Tidak Valid</h3>
        <p class="error-message">
          {{ errorMessage || 'Parameter yang diperlukan tidak lengkap atau tidak valid.' }}
        </p>
        <el-button type="primary" @click="goToHome">
          <i class="el-icon-s-home"></i>
          Kembali ke Beranda
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import axios from 'axios'

export default {
  name: 'SSOAuthorizeView',
  data() {
    return {
      loading: true,
      error: null,
      errorMessage: null,
      // Query params
      clientId: null,
      redirectUri: null,
      state: null,
      // User info
      userName: '',
      userEmail: '',
      userInitials: '',
      userNip: '',
      userJabatan: '',
      userUnitKerja: ''
    }
  },
  computed: {
    ...mapGetters('sso', {
      clientInfo: 'getClientInfo',
      ssoError: 'getError',
      authorizing: 'isAuthorizing',
      denying: 'isDenying'
    }),
    ...mapGetters('employee', {
      employeeData: 'getEmployeeData',
      employeeLoading: 'isLoading'
    })
  },
  mounted() {
    this.initializeAuthorization()
  },
  methods: {
    ...mapActions('sso', [
      'fetchClientInfo',
      'authorize',
      'deny',
      'clearError',
      'clearState'
    ]),
    ...mapActions('employee', {
      fetchEmployee: 'fetchEmployeeData'
    }),

    async initializeAuthorization() {
      try {
        // Get query parameters
        this.clientId = this.$route.query.client_id
        this.redirectUri = this.$route.query.redirect_uri
        this.state = this.$route.query.state

        // Validate required parameters
        if (!this.clientId || !this.redirectUri) {
          this.errorMessage = 'Parameter client_id dan redirect_uri wajib diisi'
          this.loading = false
          return
        }

        // Check if user is authenticated
        const isAuthenticated = this.$store.getters.isLoggedIn || 
                               localStorage.getItem('token') || 
                               sessionStorage.getItem('token')

        if (!isAuthenticated) {
          // User not logged in, redirect to login with return URL
          const currentUrl = window.location.href
          this.$router.push({
            path: '/',
            query: {
              redirect: currentUrl
            }
          })
          return
        }

        // Get user info from store or API
        await this.loadUserInfo()

        // Get client info using store action
        await this.fetchClientInfo(this.clientId)

        // Check if client has auto-approve enabled
        if (this.clientInfo && this.clientInfo.autoApprove) {
          // Auto approve - directly call backend to generate code
          await this.autoAuthorize()
        } else {
          // Show authorization form for user approval
          this.loading = false
        }
      } catch (error) {
        console.error('Error initializing authorization:', error)
        this.errorMessage = error.response?.data?.message || error.message || 'Terjadi kesalahan saat memuat informasi'
        this.loading = false
      }
    },

    async autoAuthorize() {
      try {
        // Call store action to authorize
        const result = await this.authorize({
          clientId: this.clientId,
          redirectUri: this.redirectUri,
          responseType: 'code',
          state: this.state
        })

        // Build redirect URL dengan authorization code
        const redirectUrl = new URL(this.redirectUri)
        redirectUrl.searchParams.append('code', result.code)
        if (this.state) {
          redirectUrl.searchParams.append('state', this.state)
        }

        // Redirect ke client application dengan authorization code
        console.log('Auto-authorizing, redirecting to:', redirectUrl.toString())
        window.location.href = redirectUrl.toString()
      } catch (error) {
        console.error('Error auto-authorizing:', error)
        this.errorMessage = error.message || 'Terjadi kesalahan saat melakukan otorisasi otomatis'
        this.loading = false
      }
    },

    async loadUserInfo() {
      try {
        // Fetch employee data from store (with caching)
        const employee = await this.fetchEmployee()
        
        console.log('Employee data from store:', employee)

        if (employee) {
          // Extract user info from employee data (sesuaikan dengan struktur API)
          this.userName = employee.nama || employee.name || 'Pengguna'
          this.userEmail = employee.email && employee.email !== 'Tidak ada' ? employee.email : 'Email tidak tersedia'
          this.userInitials = this.getInitials(this.userName)
          this.userNip = employee.nip || ''
          this.userJabatan = employee.jabatan && employee.jabatan !== 'Tidak ada' ? employee.jabatan : ''
          this.userUnitKerja = employee.unitKerja || employee.perEselonDua || employee.namaUnit || ''
          
          // Filter "Tidak ada" values
          if (this.userUnitKerja === 'Tidak ada') this.userUnitKerja = ''
        } else {
          // Fallback to Vuex store if API fails
          const user = this.$store.state.user.user || this.$store.state.info
          const pegawai = this.$store.state.user.pegawai || this.$store.state.pegawai

          if (user) {
            this.userName = user.nama || user.name || user.username || 'Pengguna'
            this.userEmail = user.email || 'Email tidak tersedia'
            this.userInitials = this.getInitials(this.userName)
            this.userNip = user.nip || user.username || ''
            this.userJabatan = user.jabatan || user.jabatan_nama || user.jabatanNama || ''
            this.userUnitKerja = user.unit_kerja || user.unitKerja || user.instansi || ''
          }

          if (pegawai) {
            this.userNip = pegawai.nip || pegawai.peg_nip || this.userNip
            this.userJabatan = pegawai.jabatan_nama || pegawai.jabatanNama || pegawai.jabatan || this.userJabatan
            this.userUnitKerja = pegawai.unit_kerja || pegawai.unitKerja || pegawai.instansi_induk_nama || this.userUnitKerja
          }

          if (!user) {
            this.userName = 'Pengguna'
            this.userEmail = 'Email tidak tersedia'
            this.userInitials = 'P'
          }
        }

        console.log('User info loaded:', {
          name: this.userName,
          email: this.userEmail,
          nip: this.userNip,
          jabatan: this.userJabatan,
          unitKerja: this.userUnitKerja
        })
      } catch (error) {
        console.error('Error loading user info:', error)
        // Use fallback
        this.userName = 'Pengguna'
        this.userEmail = 'Email tidak tersedia'
        this.userInitials = 'P'
      }
    },

    async handleAuthorize() {
      this.error = null
      this.clearError()

      try {
        // Call store action to authorize
        const result = await this.authorize({
          clientId: this.clientId,
          redirectUri: this.redirectUri,
          responseType: 'code',
          state: this.state
        })

        // Build redirect URL dengan authorization code
        const redirectUrl = new URL(this.redirectUri)
        redirectUrl.searchParams.append('code', result.code)
        if (this.state) {
          redirectUrl.searchParams.append('state', this.state)
        }

        // Redirect ke client application dengan authorization code
        console.log('Redirecting to:', redirectUrl.toString())
        window.location.href = redirectUrl.toString()
      } catch (error) {
        console.error('Error authorizing:', error)
        this.error = error.message || this.ssoError || 'Terjadi kesalahan saat memberikan otorisasi'
      }
    },

    async handleDeny() {
      this.error = null
      this.clearError()

      try {
        // Call store action to deny
        await this.deny()

        // Build redirect URL with error
        const url = new URL(this.redirectUri)
        url.searchParams.append('error', 'access_denied')
        url.searchParams.append('error_description', 'User denied authorization')
        if (this.state) {
          url.searchParams.append('state', this.state)
        }

        // Redirect back to client with error
        window.location.href = url.toString()
      } catch (error) {
        console.error('Error denying:', error)
        this.error = error.message || 'Terjadi kesalahan saat menolak otorisasi'
      }
    },

    getInitials(name) {
      if (!name) return '?'
      
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    goToHome() {
      this.clearState()
      this.$router.push('/')
    }
  },
  beforeDestroy() {
    // Clear SSO state when leaving the page
    this.clearError()
  }
}
</script>

<style scoped>
.sso-authorize-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Loading State */
.loading-wrapper {
  text-align: center;
  color: white;
}

.loading-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.loading-text {
  margin-top: 16px;
  font-size: 16px;
}

/* Card Styling */
.authorize-card,
.error-card {
  max-width: 520px;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.authorize-card >>> .el-card__body {
  padding: 0;
}

/* Card Header */
.card-header {
  text-align: center;
  padding: 32px 24px 24px;
  background: linear-gradient(to bottom, rgba(102, 126, 234, 0.05), transparent);
  border-bottom: 1px solid #f0f0f0;
}

.header-icon {
  margin-bottom: 16px;
}

.header-icon i {
  font-size: 64px;
  color: #409EFF;
}

.header-title {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  color: #303133;
}

/* Card Content */
.card-content {
  padding: 24px;
}

/* Client Info */
.client-info {
  margin-bottom: 24px;
}

.client-name-wrapper {
  text-align: center;
  margin-bottom: 20px;
}

.client-name-wrapper >>> .el-tag {
  font-size: 16px;
  padding: 10px 24px;
  height: auto;
}

.info-alert {
  margin-bottom: 20px;
  border-radius: 8px;
}

.info-alert >>> .el-alert__content {
  line-height: 1.6;
}

/* Permissions List */
.permissions-list {
  margin: 20px 0;
}

.permission-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.permission-item:last-child {
  border-bottom: none;
}

.permission-icon {
  flex-shrink: 0;
  margin-right: 16px;
}

.permission-icon i {
  font-size: 24px;
  color: #67C23A;
}

.permission-content {
  flex: 1;
}

.permission-title {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  margin-bottom: 4px;
}

.permission-subtitle {
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

.permission-data {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.permission-data span {
  display: inline-block;
  font-size: 12px;
}

.nip-badge,
.jabatan-badge,
.unit-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.nip-badge {
  background-color: #e3f2fd;
  color: #1976d2;
}

.jabatan-badge {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.unit-badge {
  background-color: #e8f5e9;
  color: #388e3c;
}

/* User Info Card */
.user-info-card {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.user-info-content {
  display: flex;
  align-items: center;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #409EFF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.user-initials {
  color: white;
  font-size: 18px;
  font-weight: bold;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  margin-bottom: 4px;
}

.user-email {
  font-size: 13px;
  color: #909399;
}

/* Profile Details */
.profile-details {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.profile-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #e9ecef;
}

.profile-item:last-child {
  border-bottom: none;
}

.profile-label {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.profile-value {
  font-size: 13px;
  color: #303133;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

/* Alerts */
.security-alert,
.error-alert {
  margin-bottom: 20px;
  border-radius: 8px;
}

.security-alert >>> .el-alert__content,
.error-alert >>> .el-alert__content {
  line-height: 1.6;
}

/* Additional Info */
.additional-info {
  text-align: center;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.redirect-info {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.revoke-info {
  font-size: 12px;
  color: #909399;
  margin-bottom: 0;
}

.revoke-info i {
  margin-right: 4px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.authorize-btn,
.deny-btn {
  width: 100%;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.authorize-btn >>> i,
.deny-btn >>> i {
  margin-right: 8px;
}

/* Error State */
.error-content {
  text-align: center;
  padding: 48px 24px;
}

.error-icon {
  font-size: 64px;
  color: #F56C6C;
  margin-bottom: 24px;
}

.error-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
}

.error-message {
  font-size: 14px;
  color: #909399;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Animation */
.authorize-card,
.error-card {
  animation: slideUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 600px) {
  .sso-authorize-container {
    padding: 16px;
  }
  
  .authorize-card,
  .error-card {
    border-radius: 12px;
    max-width: 100%;
  }

  .card-header {
    padding: 24px 16px 20px;
  }

  .header-icon i {
    font-size: 48px;
  }

  .header-title {
    font-size: 20px;
  }

  .card-content {
    padding: 16px;
  }

  .client-name-wrapper >>> .el-tag {
    font-size: 14px;
    padding: 8px 16px;
  }

  .permission-icon i {
    font-size: 20px;
  }

  .permission-title {
    font-size: 14px;
  }

  .permission-subtitle {
    font-size: 12px;
  }

  .permission-data {
    margin-top: 6px;
    gap: 6px;
  }

  .permission-data span {
    font-size: 11px;
  }

  .nip-badge,
  .jabatan-badge,
  .unit-badge {
    padding: 3px 8px;
    font-size: 10px;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
  }

  .user-initials {
    font-size: 16px;
  }

  .error-content {
    padding: 32px 16px;
  }
}
</style>
